import React, { useEffect, useState } from 'react';
import TwoColumnYesNoField from '../shared/TwoColumnYesNoField';

const TwoColumnDropdownField = ({ question, ids, options, fieldValues, onChange }) => (
    <div className="grid grid-cols-1 md:grid-cols-[3fr_1fr_1fr] border-t border-[var(--blue-2)] py-4 items-center">
        <div className="text-[var(--slate-grey-2)] text-sm font-medium pr-4">{question}</div>
        {ids.map((id) => (
            <select
                key={id}
                id={`field-${id}`}
                className="form-input-3-4 text-sm border rounded p-1"
                value={fieldValues[id] || ""}
                onChange={(e) => onChange(id, e.target.value)}
            >
                <option value="">Select</option>
                {options.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
            </select>
        ))}
    </div>
);

const TwoColumnTextInputField = ({ question, ids, fieldValues }) => (
    <div className="grid grid-cols-1 md:grid-cols-[3fr_1fr_1fr] py-4 items-center">
        <div className="text-[var(--slate-grey-2)] text-sm font-medium pr-4">{question}</div>
        {ids.map((id) => (
            <input
                key={id}
                type="text"
                id={`field-${id}`}
                className="form-input-3-4"
                defaultValue={fieldValues[id] || ""}
            />
        ))}
    </div>
);

const PropertyAndMoneyDetails = ({ fieldValues }) => {
    const [state, setState] = useState({});

    const mapToBoolean = (value) => {
        if (value === "Yes" || value === "Y") return true;
        if (value === "No" || value === "N") return false;
        return "";
    };
    
    useEffect(() => {
        const yesNoFields = [
            "418", "1343", "403", "1108", 
            "URLA.X84", "URLA.X85", 
            "CX.BPL.14.DAYS.OCC.1", "CX.BPL.14.DAYS.OCC.2", 
            "URLA.X86", "URLA.X87", 
            "URLA.X90", "URLA.X91", 
            "URLA.X92", "URLA.X93", 
            "URLA.X94", "URLA.X95"
        ];
    
        const initialState = {};
        Object.keys(fieldValues).forEach((key) => {
            initialState[key] = yesNoFields.includes(key) 
                ? mapToBoolean(fieldValues[key]) 
                : fieldValues[key] || "";
        });
        setState(initialState);
    }, [fieldValues]);
    

    const handleCheckboxChange = (fieldId, value) => {
        setState((prev) => {
            const newValue = prev[fieldId] === value ? "" : value; // Toggle logic (same as AboutYourFinances)
            return { ...prev, [fieldId]: newValue };
        });

        const yesElement = document.getElementById(`field-${fieldId}-yes`);
        const noElement = document.getElementById(`field-${fieldId}-no`);

        if (yesElement && noElement) {
            if (value === true) {
                yesElement.checked = true;
                noElement.checked = false;
            } else if (value === false) {
                yesElement.checked = false;
                noElement.checked = true;
            } else {
                yesElement.checked = false;
                noElement.checked = false;
            }

            const inputElement = document.getElementById(`field-${fieldId}`);
            if (inputElement) {
                inputElement.value = value === true ? "Y" : value === false ? "N" : "";
            }
        }
    };

    const handleSelectChange = (fieldId, value) => {
        setState((prev) => ({ ...prev, [fieldId]: value }));
    };

    return (
        <div className="border border-[var(--blue-2)] rounded bg-[#f9f9f9] mb-3">
            <div className="section-header">About this Property and Your Money for this Loan</div>

            <div className="grid grid-cols-1 md:grid-cols-[3fr_1fr_1fr] p-4 border-[var(--blue-2)] bg-[var(--white)]">
                <div></div>
                <div className="font-semibold text-[var(--slate-grey-2)] text-sm">Applicant</div>
                <div className="font-semibold text-[var(--slate-grey-2)] text-sm">Co-Applicant</div>
            </div>

            <div className="px-4">
                <TwoColumnYesNoField
                    question="Will you occupy the property as your primary residence?"
                    ids={["418", "1343"]}
                    fieldValues={state}
                    onChange={handleCheckboxChange}
                />

                <TwoColumnYesNoField
                    question="If YES, have you had an ownership interest in another property in the last three years? If YES, complete (1) and (2) below:"
                    ids={["403", "1108"]}
                    fieldValues={state}
                    onChange={handleCheckboxChange}
                />

                <TwoColumnDropdownField
                    question="1. What type of property did you own: primary residence (PR), FHA secondary residence (SR), second home (SH), or investment property (IP)?"
                    ids={["981", "1015"]}
                    options={[
                        { label: "Primary Residence", value: "PrimaryResidence" },
                        { label: "FHA Secondary Residence", value: "FHASecondaryResidence" },
                        { label: "Second Home", value: "SecondaryResidence" },
                        { label: "Investment Property", value: "Investment" }
                    ]}
                    fieldValues={state}
                    onChange={handleSelectChange}
                />

                <TwoColumnDropdownField
                    question="2. How did you hold title to the property: by yourself (S), jointly with your spouse (SP), or jointly with another person (O)?"
                    ids={["1069", "1070"]}
                    options={[
                        { label: "Sole", value: "Sole" },
                        { label: "Joint with Spouse", value: "JointWithSpouse" },
                        { label: "Joint with Other Than Spouse", value: "JointWithOtherThanSpouse" }
                    ]}
                    fieldValues={state}
                    onChange={handleSelectChange}
                />

                <TwoColumnYesNoField
                    question="If this is a Purchase Transaction: Do you have a family relationship or business affiliation with the seller of the property?"
                    ids={["URLA.X84", "URLA.X85"]}
                    fieldValues={state}
                    onChange={handleCheckboxChange}
                />

                <TwoColumnYesNoField
                    question="Does the applicant and/or co-applicant(s) intend to occupy the property for more than 14 days per year?"
                    ids={["CX.BPL.14.DAYS.OCC.1", "CX.BPL.14.DAYS.OCC.2"]}
                    fieldValues={state}
                    onChange={handleCheckboxChange}
                />

                <TwoColumnYesNoField
                    question="Are you borrowing any money for this real estate transaction (e.g., money for your closing costs or down payment) or obtaining any money from another party, such as the seller or realtor, that you have not disclosed on this loan application?"
                    ids={["URLA.X86", "URLA.X87"]}
                    fieldValues={state}
                    onChange={handleCheckboxChange}
                />

                <TwoColumnTextInputField
                    question="If YES, what is the amount of this money?"
                    ids={["URLA.X88", "URLA.X89"]}
                    fieldValues={state}
                />

                <TwoColumnYesNoField
                    question="Have you or will you be applying for a mortgage loan on another property (not the property securing this loan) on or before closing this transaction that is not disclosed on this loan application?"
                    ids={["URLA.X90", "URLA.X91"]}
                    fieldValues={state}
                    onChange={handleCheckboxChange}
                />

                <TwoColumnYesNoField
                    question="Have you or will you be applying for any new credit (e.g., installment loan, credit card, etc.) on or before closing this loan that is not disclosed on this application?"
                    ids={["URLA.X92", "URLA.X93"]}
                    fieldValues={state}
                    onChange={handleCheckboxChange}
                />

                <TwoColumnYesNoField
                    question="Will this property be subject to a lien that could take priority over the first mortgage lien, such as a clean energy lien paid through your property taxes (e.g., the Property Assessed Clean Energy Program)?"
                    ids={["URLA.X94", "URLA.X95"]}
                    fieldValues={state}
                    onChange={handleCheckboxChange}
                />
            </div>
        </div>
    );
};

export default PropertyAndMoneyDetails;
